// As microanimações da página de indicadores.
//
// ## A regra que governa este arquivo
//
//   **Sem animação, a tela tem de ficar no estado FINAL.**
//
// Toda função daqui é um enfeite sobre uma tela que já funciona. Se o GSAP
// não carregar, se o elemento não existir, se a pessoa pedir movimento
// reduzido -- o resultado tem de ser a tela pronta, nunca um cartão invisível
// esperando uma animação que não vai acontecer. É por isso que cada uma
// começa escrevendo o estado final e só depois decide se anima.
//
// O `gsap` entra por parâmetro em vez de ser importado aqui: assim estas
// funções têm teste sem carregar a biblioteca, e o custo dela fica no
// componente que de fato anima.

import { prefereMovimentoReduzido } from './movimentoReduzido';

/** Quanto tempo a contagem leva, em segundos. */
export const DURACAO_DA_CONTAGEM = 0.9;

/**
 * O número final, formatado como a tela mostra.
 *
 * Separado para o estado "sem animação" e o quadro final da animação saírem
 * da MESMA formatação -- senão o número pisca de "1.234" para "1234" no
 * último quadro.
 */
export function formatarNumero(valor) {
    const n = Number(valor);

    return Number.isFinite(n) ? Math.round(n).toLocaleString('pt-BR') : '0';
}

/**
 * Conta de zero até o valor, escrevendo no elemento.
 *
 * @param {HTMLElement|null} elemento
 * @param {number} valor
 * @param {object} [opcoes]
 * @param {object} [opcoes.gsap] a instância do GSAP; sem ela, só escreve
 * @param {boolean} [opcoes.reduzido] força o modo sem movimento (testes)
 * @returns {object|null} o tween, quando houver
 */
export function contarAte(elemento, valor, opcoes = {}) {
    if (!elemento) return null;

    const final = formatarNumero(valor);

    // O estado final PRIMEIRO. Se qualquer coisa abaixo falhar, o número
    // correto já está na tela.
    elemento.textContent = final;

    const reduzido = opcoes.reduzido !== undefined
        ? opcoes.reduzido
        : prefereMovimentoReduzido();

    if (reduzido || !opcoes.gsap) return null;

    const alvo = Number(valor);

    if (!Number.isFinite(alvo) || alvo === 0) return null;

    const contador = { n: 0 };

    return opcoes.gsap.to(contador, {
        n: alvo,
        duration: DURACAO_DA_CONTAGEM,
        ease: 'power2.out',
        onUpdate: () => {
            elemento.textContent = formatarNumero(contador.n);
        },
        onComplete: () => {
            // Garante o valor exato: a interpolação pode parar em 23,999.
            elemento.textContent = final;
        },
    });
}

/**
 * Faz os elementos aparecerem ao entrar na tela.
 *
 * @param {Array<HTMLElement>} elementos
 * @param {object} [opcoes] gsap, ScrollTrigger, reduzido
 * @returns {Array<object>} as animações criadas
 */
export function revelarAoRolar(elementos, opcoes = {}) {
    const lista = (Array.isArray(elementos) ? elementos : []).filter(Boolean);

    if (lista.length === 0) return [];

    const reduzido = opcoes.reduzido !== undefined
        ? opcoes.reduzido
        : prefereMovimentoReduzido();

    /*
     * Sem movimento, a tela fica como sempre foi: tudo visível, sem
     * deslocamento. Note que NADA foi escondido antes desta checagem -- o
     * `opacity: 0` só existe dentro do `from`, que o GSAP desfaz ao animar.
     * Esconder no CSS e revelar no script deixaria a tela em branco para
     * quem bloqueia script ou para quem pediu movimento reduzido.
     */
    if (reduzido || !opcoes.gsap) return [];

    return lista.map((elemento, indice) => opcoes.gsap.from(elemento, {
        opacity: 0,
        y: 24,
        duration: 0.5,
        ease: 'power2.out',
        // Escalonado por posição, não por tempo fixo: o primeiro cartão não
        // espera os outros.
        delay: Math.min(indice, 4) * 0.06,
        scrollTrigger: opcoes.ScrollTrigger ? {
            trigger: elemento,
            start: 'top 92%',
            once: true,
            /*
             * `scroller` é obrigatório aqui: nesta tela quem rola NÃO é a
             * janela, é uma caixa interna com `overflow: auto`. Sem dizer
             * isso, o gatilho observa a rolagem da janela -- que nunca se
             * move -- e as seções abaixo da dobra ficariam invisíveis para
             * sempre. É o pior defeito possível numa animação de entrada.
             */
            scroller: opcoes.scroller || undefined,
        } : undefined,
    }));
}

/**
 * Desfaz o que foi criado. Chamado na limpeza do efeito.
 *
 * Animação que sobrevive ao componente continua mexendo num elemento que
 * saiu da árvore -- e no React 17 isso aparece como aviso de atualização em
 * componente desmontado, ou simplesmente como vazamento.
 */
export function pararAnimacoes(animacoes) {
    (Array.isArray(animacoes) ? animacoes : [animacoes]).forEach((a) => {
        if (!a) return;

        if (a.scrollTrigger && typeof a.scrollTrigger.kill === 'function') {
            a.scrollTrigger.kill();
        }

        if (typeof a.kill === 'function') a.kill();
    });
}
